// Dialer country gate: the picker lists EVERY country, and this decides what
// the dialer says before it lets a call to `iso` go out. Pure and advisory —
// the server gates (TwiML app, Telnyx bridge, /api/calls) stay the authority.
//
// Precedence (owner decision, 2026-10):
//   0. a country switched off at the carrier → "contact support", first because
//      nothing the customer does can change it: not a test call, not KYC
//   1. a free test destination (verified number, AI demo line) → allowed
//   2. a country the account isn't entitled to → verify, then ask support (even
//      on a trial: identity verification is the bigger blocker). Verifying alone
//      unblocks nothing (canCallIsoCountry never looks at KYC); if the account is
//      ALREADY verified, only support is left → "not available, contact support"
//   3. an entitled country while trialing → "paid accounts" notice. Domestic
//      (US/CA/GB) destinations keep the #1537 trial message instead — the
//      "other countries" wording only makes sense abroad.
//   4. otherwise allowed.
import { normalizeCountryCode, type Capabilities } from '@shared/capabilities';
import { getPhoneCountries, type PhoneCountry } from './phoneCountries';
import { TRIAL_OUTBOUND_MESSAGE } from './freeTestCalls';

/** Same words as the server (server/lib/countryKycMessage) and limecall-dashboard's copy. */
export const kycCountryMessage = (country?: string) => `Verify your ID, then contact support to unblock ${country || 'this country'}`;
export const TRIAL_COUNTRY_MESSAGE = 'Calling other countries is available on paid accounts. Start your plan to call all allowed countries.';
export const unavailableCountryMessage = (country: string) => `Calls to ${country} aren't enabled on your account yet.`;

const DOMESTIC = new Set(['US', 'CA', 'GB']);

export type CountryGate =
  | { kind: 'allowed' }
  | { kind: 'kyc'; message: string; iso?: string; countryName?: string }
  | { kind: 'unavailable'; message: string; reason: 'carrier_country_disabled' | 'country_not_enabled'; iso?: string; countryName?: string }
  | { kind: 'trial'; message: string; domestic: boolean };

/**
 * Is the account ENTITLED to call this ISO country — its plan regions plus the
 * admin's per-country exceptions — ignoring the trial/outbound switch, which
 * this gate reports separately?
 */
export function countryEntitled(caps: Pick<Capabilities, 'voice'> | null | undefined, iso: string | null | undefined): boolean {
  const code = String(iso ?? '').trim().toUpperCase();
  if (!caps?.voice || !code) return false;
  if (caps.voice.allowedIsoCountries?.includes(code)) return true;
  const region = normalizeCountryCode(code);
  return !!region && (caps.voice.allowedCountries || []).includes(region);
}

export function decideCountryGate(p: {
  iso: string | null | undefined;
  countryName?: string;
  isTestDestination: boolean;
  entitled: boolean;
  trialing: boolean;
  kycVerified: boolean;
  /** Off in the carrier's geographic permissions (GET /api/capabilities/outbound-countries). */
  carrierDisabled?: boolean;
}): CountryGate {
  // What the support link needs: the country, never the number.
  const where = { ...(p.iso ? { iso: String(p.iso).toUpperCase() } : {}), ...(p.countryName ? { countryName: p.countryName } : {}) };
  const unavailable = unavailableCountryMessage(p.countryName || String(p.iso || 'this country'));
  if (p.carrierDisabled) return { kind: 'unavailable', reason: 'carrier_country_disabled', message: unavailable, ...where };
  if (p.isTestDestination) return { kind: 'allowed' };
  if (!p.entitled) {
    return p.kycVerified
      ? { kind: 'unavailable', reason: 'country_not_enabled', message: unavailable, ...where }
      : { kind: 'kyc', message: kycCountryMessage(p.countryName), ...where };
  }
  if (p.trialing) {
    const domestic = DOMESTIC.has(String(p.iso || '').toUpperCase());
    return { kind: 'trial', domestic, message: domestic ? TRIAL_OUTBOUND_MESSAGE : TRIAL_COUNTRY_MESSAGE };
  }
  return { kind: 'allowed' };
}

/** Every country (libphonenumber metadata), entitled ones first, each group by name. */
export function pickerCountries(caps: Pick<Capabilities, 'voice'> | null | undefined, all: PhoneCountry[] = getPhoneCountries()) {
  const rows = all.map((c) => ({ ...c, entitled: countryEntitled(caps, c.iso) }));
  return [...rows.filter((c) => c.entitled), ...rows.filter((c) => !c.entitled)];
}

/** Start the account's identity verification (hosted Stripe/Didit page). Owner/admin only server-side. */
export async function startIdentityVerification(post: (url: string, body: unknown) => Promise<any>): Promise<{ url?: string; error?: string }> {
  try {
    const r = await post('/api/account/kyc/start', { kind: 'person' });
    return r?.url ? { url: String(r.url) } : { error: 'Verification is not available right now. Please contact support.' };
  } catch (e: any) {
    const msg = String(e?.message || '');
    return { error: /403/.test(msg) ? 'Ask your account owner or an admin to complete ID verification.' : (msg.replace(/^\d{3}:\s*/, '') || 'Could not start verification.') };
  }
}
